import type { PostgrestError, RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import { APPROVAL_INERT_EVENT_STATUSES } from '../domain/events';
import { recordRealtimeEvent } from '../lib/realtimeTelemetry';

type ClientOption = { supabase?: SupabaseClient };

export type WorkspaceStats = { events: number; customers: number; venues: number; users: number };

export type PendingApprovalAssignment = {
  assignmentId: string;
  approvalId: string;
  eventId: string;
  eventName: string | null;
  eventCode: string | null;
  policyName: string | null;
  requestedAt: string | null;
  amount: number | null;
  basis: 'pre_discount' | 'post_discount' | null;
};

export type PendingApprovalsResult = { items: PendingApprovalAssignment[]; total: number };

export async function fetchWorkspaceStats(
  options?: ClientOption & {
    includeEvents?: boolean;
    includeCustomers?: boolean;
    includeVenues?: boolean;
    includeUsers?: boolean;
  },
): Promise<{ data: WorkspaceStats; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const includeEvents = options?.includeEvents ?? true;
  const includeCustomers = options?.includeCustomers ?? true;
  const includeVenues = options?.includeVenues ?? true;
  const includeUsers = options?.includeUsers ?? false;
  const [eventsResp, venuesResp, customersResp, usersResp] = await Promise.all([
    includeEvents
      ? supabase.from('events').select('*', { count: 'planned', head: true }).is('deleted_at', null)
      : Promise.resolve({ count: 0 } as any),
    includeVenues
      ? supabase.from('venues').select('*', { count: 'planned', head: true }).is('deleted_at', null)
      : Promise.resolve({ count: 0 } as any),
    includeCustomers
      ? supabase.from('clients').select('*', { count: 'planned', head: true }).is('deleted_at', null)
      : Promise.resolve({ count: 0 } as any),
    includeUsers
      ? supabase.from('user_profiles').select('*', { count: 'exact', head: true })
      : Promise.resolve({ count: 0 } as any),
  ]);
  const error = (includeEvents ? (eventsResp as any)?.error ?? null : null)
    || (includeVenues ? (venuesResp as any)?.error ?? null : null)
    || (includeCustomers ? (customersResp as any)?.error ?? null : null)
    || (includeUsers ? (usersResp as any)?.error ?? null : null)
    || null;
  return {
    data: {
      events: (eventsResp as any)?.count || 0,
      customers: (customersResp as any)?.count || 0,
      venues: (venuesResp as any)?.count || 0,
      users: (usersResp as any)?.count || 0,
    },
    error,
  };
}

export async function fetchPendingApprovals(
  profileId: string,
  options?: ClientOption,
): Promise<{ data: PendingApprovalsResult; error: PostgrestError | null }> {
  if (!profileId) return { data: { items: [], total: 0 }, error: null };
  const supabase = options?.supabase ?? getSupabaseClient();
  const today = new Date().toISOString().slice(0, 10);
  const { data, error, count } = await supabase
    .from('approval_assignments')
    .select(
      `
        approval_assignment_id,
        approval_id,
        created_at,
        dismissed_until,
        approvals:approvals!inner (
          approval_id,
          status,
          requested_at,
          amount_post_discount,
          amount_pre_discount,
          basis,
          event_id,
          event:events!approvals_event_id_fkey!inner (
            id,
            name,
            event_code,
            status
          ),
          policy:approval_policies (
            approval_policy_id,
            name
          )
        )
      `,
      { count: 'exact' },
    )
    .eq('user_profile_id', profileId)
    .eq('status', 'pending')
    .or(`dismissed_until.is.null,dismissed_until.lt.${today}`)
    .eq('approvals.kind', 'quote')
    .eq('approvals.status', 'pending')
    // A cancelled/lost/void/invoiced event no longer needs approving, so its
    // assignments must drop out of the queue even if the rows still exist.
    .not('approvals.event.status', 'in', `(${APPROVAL_INERT_EVENT_STATUSES.join(',')})`)
    .order('created_at', { ascending: true })
    .limit(5);
  if (error) return { data: { items: [], total: 0 }, error };
  const mapped = (data ?? [])
    .map((row: any) => {
      const approval = row.approvals as any;
      const event = approval?.event as any;
      if (!event || !event.id) return null;
      const amountRaw = approval.amount_post_discount ?? approval.amount_pre_discount ?? null;
      const amountNumeric = amountRaw == null ? null : Number(amountRaw);
      return {
        assignmentId: row.approval_assignment_id,
        approvalId: row.approval_id,
        eventId: event.id,
        eventName: event.name ?? null,
        eventCode: event.event_code ?? null,
        policyName: approval.policy?.name ?? null,
        requestedAt: approval.requested_at ?? null,
        amount: amountNumeric,
        basis: approval.basis ?? null,
      } as PendingApprovalAssignment;
    })
    .filter(Boolean) as PendingApprovalAssignment[];
  return {
    data: { items: mapped, total: typeof count === 'number' ? count : mapped.length },
    error: null,
  };
}

export async function dismissApprovalAssignment(
  assignmentId: string,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('quote_dismiss_assignment', { _assignment_id: assignmentId });
  return error ?? null;
}

const DASHBOARD_FEED_REFRESH_MIN_MS = 5000;

export function subscribeDashboardFeed(params: {
  supabase?: SupabaseClient;
  profileId?: string | null;
  onChange: () => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const profileId = params.profileId?.trim() || null;
  let channel: RealtimeChannel | null = null;
  let closed = false;
  let pendingRefresh: ReturnType<typeof setTimeout> | null = null;
  let lastRefreshAt = 0;

  const flushChange = () => {
    pendingRefresh = null;
    if (closed) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    lastRefreshAt = Date.now();
    params.onChange();
  };

  const scheduleChange = () => {
    if (closed) return;
    const now = Date.now();
    const remainingMs = DASHBOARD_FEED_REFRESH_MIN_MS - (now - lastRefreshAt);
    if (remainingMs <= 0) {
      flushChange();
      return;
    }
    if (pendingRefresh) return;
    pendingRefresh = setTimeout(flushChange, remainingMs);
  };

  const wrap = (table: string) => () => {
    recordRealtimeEvent({ source: 'dashboard-feed', table, eventType: '*' });
    scheduleChange();
  };

  const unsubscribeChannel = () => {
    if (!channel) return;
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
    channel = null;
  };

  const subscribeVisible = () => {
    if (closed || channel) return;
    if (typeof document !== 'undefined' && document.hidden) return;

    let next = supabase
      .channel(`dashboard-feed:${profileId ?? 'anonymous'}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dashboard_broadcasts' }, wrap('dashboard_broadcasts'));

    if (profileId) {
      next = next.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'quote_chat_mentions',
          filter: `mentioned_profile_id=eq.${profileId}`,
        },
        wrap('quote_chat_mentions'),
      );
      next = next.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'dashboard_tasks',
          filter: `target_profile_id=eq.${profileId}`,
        },
        wrap('dashboard_tasks'),
      );
      next = next.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'dashboard_notifications',
          filter: `target_profile_id=eq.${profileId}`,
        },
        wrap('dashboard_notifications'),
      );
    }

    channel = next.subscribe();
  };

  const handleVisibilityChange = () => {
    if (typeof document === 'undefined') return;
    if (document.hidden) {
      unsubscribeChannel();
      if (pendingRefresh) {
        clearTimeout(pendingRefresh);
        pendingRefresh = null;
      }
      return;
    }
    subscribeVisible();
    scheduleChange();
  };

  subscribeVisible();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  return () => {
    closed = true;
    if (pendingRefresh) {
      clearTimeout(pendingRefresh);
      pendingRefresh = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    unsubscribeChannel();
  };
}
